import {
  AppointmentDevice,
  DeviceCategory,
  DeviceConfig,
  ElectrodePolarity,
  LoretaMode,
  NfElectrode,
  NfWave,
  NfWaveBand,
  NfWaveMode,
  StaffDevice,
  StimulationCategory,
} from "../services/types/device.types";
import { LORETA_OPTIONS } from "./loretaOptions";
import { tWithKeyAttr as t } from "../i18nHelper";

/** 10-20 montage sites, ordered to match the head map the institute supplied. */
export const ELECTRODE_SITES = [
  "Fp1", "Fp2", "F7", "F3", "Fz", "F4", "F8",
  "T3", "C3", "Cz", "C4", "T4", "T5", "P3", "Pz", "P4", "T6",
  "O1", "Oz", "O2", "A1", "A2",
] as const;

export const NF_WAVE_BANDS: { key: NfWaveBand; labelKey: string }[] = [
  { key: "delta", labelKey: "DEVICE_WAVE_DELTA" },
  { key: "theta", labelKey: "DEVICE_WAVE_THETA" },
  { key: "alpha", labelKey: "DEVICE_WAVE_ALPHA" },
  { key: "beta", labelKey: "DEVICE_WAVE_BETA" },
  { key: "highbeta", labelKey: "DEVICE_WAVE_HIGH_BETA" },
  { key: "custom", labelKey: "DEVICE_WAVE_CUSTOM" },
];

export const NF_WAVE_MODES: { value: NfWaveMode; labelKey: string }[] = [
  { value: "go", labelKey: "DEVICE_WAVE_MODE_GO" },
  { value: "stop", labelKey: "DEVICE_WAVE_MODE_STOP" },
  { value: "ignore", labelKey: "DEVICE_WAVE_MODE_IGNORE" },
];

export const ELECTRODE_POLARITIES: { value: ElectrodePolarity; labelKey: string }[] = [
  { value: "anode", labelKey: "DEVICE_ANODE" },
  { value: "cathode", labelKey: "DEVICE_CATHODE" },
];

export const DEVICE_CATEGORIES: { value: DeviceCategory; labelKey: string }[] = [
  { value: "nf", labelKey: "DEVICE_CATEGORY_NF" },
  { value: "loreta", labelKey: "DEVICE_CATEGORY_LORETA" },
  { value: "scan", labelKey: "DEVICE_CATEGORY_SCAN" },
  { value: "tdcs", labelKey: "DEVICE_CATEGORY_TDCS" },
  { value: "tacs", labelKey: "DEVICE_CATEGORY_TACS" },
];

/** LORETA catalog names as the institute supplied them — clinical terms, not translated. */
export const LORETA_MODE_DISPLAY: Record<LoretaMode, string> = {
  symptoms: "Symptoms",
  networks: "Networks",
  dodva: "DoD/VA",
};

/** Canonical display casing per capability (locale-independent, e.g. "tDCS" not "TDCS"). */
export const DEVICE_CATEGORY_DISPLAY: Record<DeviceCategory, string> = {
  nf: "Neurofeedback",
  loreta: "Loreta",
  scan: "Scan",
  tdcs: "tDCS",
  tacs: "tACS",
};

export const DEFAULT_DURATION_MINUTES = 20;
export const DEFAULT_INTENSITY_MA = 2;
export const MAX_INTENSITY_MA = 2;
export const DEFAULT_WEEKLY_FREQUENCY = 1;
export const STIMULATION_ELECTRODE_COUNT = 2;

const STIMULATION_CATEGORIES: StimulationCategory[] = ["tdcs", "tacs"];

export function isStimulationCategory(category?: DeviceCategory): category is StimulationCategory {
  return !!category && (STIMULATION_CATEGORIES as DeviceCategory[]).includes(category);
}

/** tACS is the only modality that carries a stimulation frequency. */
export function supportsFrequency(category?: DeviceCategory): boolean {
  return category === "tacs";
}

export function getDefaultRegistrationConfig(): DeviceConfig {
  return { capabilities: [] };
}

/** Resolve capabilities from registration config (supports legacy single category). */
export function getDeviceCapabilities(raw?: DeviceConfig | null): DeviceCategory[] {
  if (raw?.capabilities?.length) return raw.capabilities;
  if (raw?.category) return [raw.category];
  return [];
}

/** A device's own record: which modalities it supports and where it stands. Nothing session-level. */
export function normalizeRegistrationConfig(raw?: DeviceConfig | null): DeviceConfig {
  return {
    capabilities: getDeviceCapabilities(raw),
    treatmentRoomId: raw?.treatmentRoomId ?? null,
  };
}

export function formatCapabilitiesLabel(caps: DeviceCategory[]): string {
  return caps.map((c) => DEVICE_CATEGORY_DISPLAY[c] ?? c).join(" + ");
}

/** A fresh electrode with every band present and left on "ignore". */
export function getDefaultNfWaves(): NfWave[] {
  return NF_WAVE_BANDS.map((b) => ({ band: b.key, mode: "ignore" as NfWaveMode }));
}

export function getDefaultDeviceConfig(category: DeviceCategory = "nf"): DeviceConfig {
  // A scan is a single measurement, not a course: no montage, no length, no cadence. Choosing
  // the modality is the whole of it.
  if (category === "scan") {
    return { category };
  }

  if (category === "loreta") {
    return {
      category,
      loretaMode: undefined,
      loretaSelections: [],
      durationMinutes: DEFAULT_DURATION_MINUTES,
    };
  }

  if (isStimulationCategory(category)) {
    return {
      category,
      electrodes: {},
      intensityMa: DEFAULT_INTENSITY_MA,
      sham: false,
      notes: "",
      durationMinutes: DEFAULT_DURATION_MINUTES,
    };
  }

  return {
    category,
    nfElectrodes: [],
    durationMinutes: DEFAULT_DURATION_MINUTES,
  };
}

/**
 * Drops fields that do not belong to the given modality and fills the ones that do, so
 * switching device type never carries stale values into the saved config.
 */
export function normalizeDeviceConfig(raw?: DeviceConfig | null): DeviceConfig {
  const category = raw?.category ?? "nf";

  if (category === "scan") {
    return { category };
  }

  if (category === "loreta") {
    const mode = raw?.loretaMode;
    const allowed = mode ? LORETA_OPTIONS[mode] : [];
    return {
      category,
      loretaMode: mode,
      loretaSelections: (raw?.loretaSelections ?? []).filter((s) => allowed.includes(s)),
      durationMinutes: raw?.durationMinutes ?? DEFAULT_DURATION_MINUTES,
    };
  }

  if (isStimulationCategory(category)) {
    return {
      category,
      electrodes: { ...(raw?.electrodes ?? {}) },
      intensityMa: raw?.intensityMa ?? DEFAULT_INTENSITY_MA,
      sham: raw?.sham ?? false,
      frequencyHz: supportsFrequency(category) ? raw?.frequencyHz : undefined,
      notes: raw?.notes ?? "",
      durationMinutes: raw?.durationMinutes ?? DEFAULT_DURATION_MINUTES,
    };
  }

  return {
    category,
    nfElectrodes: (raw?.nfElectrodes ?? []).map(normalizeNfElectrode),
    // Read back so a protocol saved before the change keeps its number; nothing writes it now.
    weeklyFrequency: raw?.weeklyFrequency,
    appointmentsSequenceId: raw?.appointmentsSequenceId ?? null,
    durationMinutes: raw?.durationMinutes ?? DEFAULT_DURATION_MINUTES,
  };
}

function normalizeNfElectrode(electrode: NfElectrode): NfElectrode {
  return {
    site: electrode.site,
    waves: getDefaultNfWaves().map((wave) => {
      const existing = electrode.waves?.find((w) => w.band === wave.band);
      return existing ? { ...wave, ...existing } : wave;
    }),
  };
}

export function isNfSiteSelected(config: DeviceConfig, site: string): boolean {
  return (config.nfElectrodes ?? []).some((e) => e.site === site);
}

export function toggleNfSite(config: DeviceConfig, site: string): NfElectrode[] {
  const electrodes = config.nfElectrodes ?? [];
  if (electrodes.some((e) => e.site === site)) {
    return electrodes.filter((e) => e.site !== site);
  }
  // Keep montage order stable so the summary always reads front-to-back.
  const order: readonly string[] = ELECTRODE_SITES;
  return [...electrodes, { site, waves: getDefaultNfWaves() }].sort(
    (a, b) => order.indexOf(a.site) - order.indexOf(b.site),
  );
}

export function updateNfWave(
  config: DeviceConfig,
  site: string,
  band: NfWaveBand,
  patch: Partial<NfWave>,
): NfElectrode[] {
  return (config.nfElectrodes ?? []).map((electrode) =>
    electrode.site === site
      ? {
          ...electrode,
          waves: electrode.waves.map((w) => (w.band === band ? { ...w, ...patch } : w)),
        }
      : electrode,
  );
}

/** Bands actually part of the protocol for a site, in display order. */
export function getActiveNfWaves(electrode: NfElectrode): NfWave[] {
  return electrode.waves.filter((w) => w.mode !== "ignore");
}

/**
 * Click behaviour for a stimulation montage: the first click makes the site the anode, the
 * second makes it the cathode, and clicking a selected site clears it. Beyond two sites the
 * click is ignored — the protocol allows exactly one pair.
 */
export function toggleStimulationSite(
  electrodes: Record<string, ElectrodePolarity>,
  site: string,
): Record<string, ElectrodePolarity> {
  if (electrodes[site]) {
    const { [site]: _removed, ...rest } = electrodes;
    return rest;
  }

  if (Object.keys(electrodes).length >= STIMULATION_ELECTRODE_COUNT) return electrodes;

  const hasAnode = Object.values(electrodes).includes("anode");
  return { ...electrodes, [site]: hasAnode ? "cathode" : "anode" };
}

export function setStimulationPolarity(
  electrodes: Record<string, ElectrodePolarity>,
  site: string,
  polarity: ElectrodePolarity,
): Record<string, ElectrodePolarity> {
  const next: Record<string, ElectrodePolarity> = { ...electrodes, [site]: polarity };

  // Only one pair exists, so giving one site a polarity forces the other to the opposite.
  const other = Object.keys(next).find((s) => s !== site);
  if (other) next[other] = polarity === "anode" ? "cathode" : "anode";

  return next;
}

export function getPolarityElectrodeSummary(
  electrodes?: Record<string, ElectrodePolarity>,
): { anodeSites: string[]; cathodeSites: string[] } {
  const anodeSites: string[] = [];
  const cathodeSites: string[] = [];

  Object.entries(electrodes ?? {}).forEach(([site, state]) => {
    if (state === "anode") anodeSites.push(site);
    if (state === "cathode") cathodeSites.push(site);
  });

  return { anodeSites, cathodeSites };
}

export function isDeviceContact(contact: {
  deviceConfig?: DeviceConfig | null;
  relation?: string;
  roleId?: number;
}): boolean {
  return contact.roleId === 7 || Boolean(contact.deviceConfig) || contact.relation === "device";
}

export function staffDeviceToAppointmentDevice(
  device: StaffDevice,
  sessionConfig: DeviceConfig,
): AppointmentDevice {
  return {
    ...normalizeDeviceConfig(sessionConfig),
    deviceContactId: device.deviceContactId,
    deviceContactGuid: device.deviceContactGuid,
    deviceName: device.deviceName,
    // Captured now rather than resolved on read, so moving the device later does not rewrite
    // where past appointments took place.
    treatmentRoomId: device.deviceConfig?.treatmentRoomId ?? null,
    treatmentRoomName: device.treatmentRoomName ?? null,
  };
}

const CUSTOM_RANGE_PATTERN = /^\d+(\.\d+)?\s*-\s*\d+(\.\d+)?$/;

export function validateDeviceConfig(config: DeviceConfig): string | null {
  const category = config.category;
  if (!category) return "DEVICE_CATEGORY_REQUIRED";

  // A scan only needs a duration — the site list is optional, so there is nothing else to check.
  if (category === "scan") return validateDuration(config);
  if (category === "loreta") return validateLoreta(config);
  if (isStimulationCategory(category)) return validateStimulation(config, category);
  return validateNeurofeedback(config);
}

function validateNeurofeedback(config: DeviceConfig): string | null {
  const electrodes = config.nfElectrodes ?? [];
  if (electrodes.length === 0) return "DEVICE_ELECTRODE_REQUIRED";

  const active = electrodes.flatMap(getActiveNfWaves);
  if (active.length === 0) return "DEVICE_WAVE_REQUIRED";

  const badCustom = active.some(
    (w) => w.band === "custom" && !CUSTOM_RANGE_PATTERN.test((w.customRange ?? "").trim()),
  );
  if (badCustom) return "DEVICE_CUSTOM_RANGE_INVALID";

  // The cadence is no longer asked here, and it is optional: a protocol may leave it to whoever
  // plans the series.
  return validateDuration(config);
}

function validateLoreta(config: DeviceConfig): string | null {
  if (!config.loretaMode) return "LORETA_MODE_REQUIRED";
  if ((config.loretaSelections ?? []).length === 0) return "LORETA_SELECTION_REQUIRED";
  return validateDuration(config);
}

function validateStimulation(config: DeviceConfig, category: DeviceCategory): string | null {
  const electrodes = config.electrodes ?? {};
  if (Object.keys(electrodes).length !== STIMULATION_ELECTRODE_COUNT) {
    return "DEVICE_TWO_ELECTRODES_REQUIRED";
  }

  const { anodeSites, cathodeSites } = getPolarityElectrodeSummary(electrodes);
  if (anodeSites.length !== 1 || cathodeSites.length !== 1) return "DEVICE_POLARITY_REQUIRED";

  const intensity = config.intensityMa;
  if (intensity === undefined || intensity < 0 || intensity > MAX_INTENSITY_MA) {
    return "DEVICE_INTENSITY_INVALID";
  }

  if (supportsFrequency(category) && (!config.frequencyHz || config.frequencyHz <= 0)) {
    return "DEVICE_FREQUENCY_REQUIRED";
  }

  return validateDuration(config);
}

function validateDuration(config: DeviceConfig): string | null {
  if (!config.durationMinutes || config.durationMinutes < 1) return "DEVICE_DURATION_REQUIRED";
  return null;
}

export function getDeviceSummaryLabel(device?: AppointmentDevice | DeviceConfig | null): string {
  if (!device) return "";
  const name = "deviceName" in device ? (device as AppointmentDevice).deviceName ?? "" : "";
  const caps = getDeviceCapabilities(device);

  if (device.category) {
    const modality = DEVICE_CATEGORY_DISPLAY[device.category] ?? device.category;
    return name ? `${name} — ${modality}` : modality;
  }
  if (caps.length) {
    const capLabel = formatCapabilitiesLabel(caps);
    return name ? `${name} (${capLabel})` : capLabel;
  }
  // deviceName is optional, and the signature promises a string.
  return name ?? "";
}

/**
 * What a device configuration will actually apply: modality, montage and duration. Deliberately
 * excludes the device name, so it reads correctly for a saved protocol that is not yet bound to
 * a specific device.
 */
export function getDeviceConfigSummary(config?: DeviceConfig | null): string {
  if (!config?.category) return "";

  const parts = [DEVICE_CATEGORY_DISPLAY[config.category] ?? config.category];

  const montage = getMontageSummary(config);
  if (montage.length > 0) parts.push(montage.join(" | "));

  return parts.join(" · ");
}

/**
 * Headline-only version of {@link getDeviceConfigSummary}: the modality plus counts and key
 * numbers, never the montage itself. For places that list protocols rather than configure one,
 * where a full electrode-by-electrode string is noise.
 *
 * Returns the parts so the caller can join them with whatever separator fits.
 */
export function getDeviceConfigHeadlineParts(config?: DeviceConfig | null): string[] {
  if (!config?.category) return [];

  const parts = [DEVICE_CATEGORY_DISPLAY[config.category] ?? config.category];

  if (config.category === "loreta") {
    if (config.loretaMode) parts.push(LORETA_MODE_DISPLAY[config.loretaMode]);
    const count = config.loretaSelections?.length ?? 0;
    if (count > 0) parts.push(`${count} ${t("SELECTIONS_SHORT")}`);
  } else if (isStimulationCategory(config.category)) {
    if (supportsFrequency(config.category) && config.frequencyHz) {
      parts.push(`${config.frequencyHz} Hz`);
    }
    if (config.intensityMa !== undefined) parts.push(`${config.intensityMa} mA`);
    if (config.sham) parts.push(`SHAM ${t("DEVICE_SHAM_ON")}`);
  } else {
    const count = config.nfElectrodes?.length ?? 0;
    if (count > 0) parts.push(`${count} ${t("ELECTRODES_SHORT")}`);
  }

  if (config.durationMinutes) parts.push(`${config.durationMinutes} ${t("MINUTES_SHORT")}`);

  return parts;
}

/** Human-readable montage summary, e.g. "Cz: go α, stop θ" or "F3 anode / F4 cathode". */
export function getMontageSummary(config?: DeviceConfig | null): string[] {
  if (!config) return [];

  if (config.category === "loreta") {
    return config.loretaSelections ?? [];
  }

  if (isStimulationCategory(config.category)) {
    return Object.entries(config.electrodes ?? {}).map(([site, polarity]) => `${site}:${polarity}`);
  }

  return (config.nfElectrodes ?? []).map(
    (e) => `${e.site}:${getActiveNfWaves(e).map((w) => `${w.mode} ${waveLabel(w)}`).join(", ") || "—"}`,
  );
}

function waveLabel(wave: NfWave): string {
  return wave.band === "custom" ? (wave.customRange ?? "custom") : wave.band;
}
